/* eslint-disable no-nested-ternary */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CardElement } from '@stripe/react-stripe-js';
import { Dispatch, FocusEvent, SetStateAction, useRef, useState } from 'react';
import { Icon, Typography } from 'beek-design-system';
import Avo from 'src/tracking/Avo';
import styled from 'styled-components';
import styles from './styles.module.css';

type InputProps = {
  placeholder?: string;
  type: string;
  name: string;
  isValid: boolean;
  width?: string;
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  setInputValue?: Dispatch<SetStateAction<string>>;
  labelError?: string;
  setInputPasswordType?: Dispatch<SetStateAction<string>>;
  showLabelError?: boolean;
  cardElement?: boolean;
  updateValues?: (e: any) => void;
  value: string;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
};

type MainContainerProps = {
  marginTop?: string;
  marginBottom?: string;
  marginLeft?: string;
  marginRight?: string;
  width?: string;
};

type InputTextProps = {
  placeholder?: string;
  type: string;
  width?: string;
  isValid: boolean;
};

type IconContainerProps = {
  showErrorLabel?: boolean;
  alwaysShowErrorLabel?: boolean;
};

type CardElementProps = {
  cardError?: string | boolean | undefined;
  mouseOnCardElement?: boolean;
};

const defaultProps = {
  placeholder: '',
  marginTop: '0rem',
  marginBottom: '0rem',
  marginLeft: '0rem',
  marginRight: '0rem',
  width: '100%',
  setInputValue: (): void => {},
  labelError: '',
  setInputPasswordType: (): void => {},
  showLabelError: false,
  cardElement: false,
  updateValues: (): void => {},
  onBlur: (): void => {},
  onFocus: (): void => {},
};

const InputText = styled.input<InputTextProps>`
  width: ${(props): string => (props.width ? props.width : '100%')};
  height: 2.5rem;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  border: 0.063rem solid
    ${(props): string | undefined =>
      props.isValid ? 'var(--gray-35)' : 'var(--medium-red)'};
  border-radius: 0.375rem;
  outline: none;
  padding-left: 0.625rem;
  font-family: var(--primary-font);
  ::placeholder {
    font-family: var(--primary-font);
    font-style: normal;
    font-weight: 400;
    font-size: 0.75rem;
    color: 'var(--gray-15)';
    @media ${({ theme }): string => theme.devices.mobileM} {
      font-size: 0.875rem;
    }
  }
  :focus {
    border: 0.0625rem solid var(--beek-blue);
  }
`;
const MainContainer = styled.div<MainContainerProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${(props): string => (props.width ? props.width : '100%')};
  margin-top: ${(props): string | undefined => props.marginTop};
  margin-bottom: ${(props): string | undefined => props.marginBottom};
  margin-left: ${(props): string | undefined => props.marginLeft};
  margin-right: ${(props): string | undefined => props.marginRight};
`;
const ShowPasswordContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
`;
const IconContainer = styled.div<IconContainerProps>`
  position: absolute;
  right: 10px;
  top: 10px;
`;
const LabelError = styled(Typography)`
  display: flex;
  flex-direction: row-reverse;
`;

const CustomCardElement = styled.div<CardElementProps>`
  height: 2.5rem;
  width: 100%;
  -webkit-box-sizing: border-box;
  -moz-box-sizing: border-box;
  box-sizing: border-box;
  border: 0.063rem solid
    ${(props): string =>
      props.cardError
        ? 'var(--medium-red)'
        : props.mouseOnCardElement
        ? 'var(--beek-blue)'
        : 'var(--gray-35)'};
  border-radius: 0.375rem;
  outline: none;
  padding-left: 0.625rem;
  padding-top: 0.625rem;
`;

const InputForm = (props: InputProps): JSX.Element => {
  const {
    placeholder,
    width,
    marginTop,
    marginBottom,
    marginRight,
    marginLeft,
    name,
    type,
    setInputPasswordType,
    setInputValue,
    isValid,
    labelError,
    showLabelError,
    cardElement,
    updateValues,
    value,
    onBlur,
    onFocus,
  } = props;
  const mainContainerStyles = {
    marginTop,
    marginBottom,
    marginRight,
    marginLeft,
    width,
  };
  const inputStyles = {
    placeholder,
    width,
    type,
    isValid,
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);
  const [onCardElement, setOnCardElement] = useState(false);

  const handleFocus = (e: FocusEvent<HTMLInputElement>): void => {
    setFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>): void => {
    setFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const labelErrorValidations =
    (!isValid && !focused) || (cardElement && labelError);

  const changeInputType = (): void => {
    if (inputRef.current?.type === 'password') {
      if (setInputPasswordType) setInputPasswordType('text');
      setShowPassword(true);
    } else {
      if (setInputPasswordType) setInputPasswordType('password');
      setShowPassword(false);
    }
  };

  const cardElementOptions = {
    style: {
      base: {
        fontFamily: 'var(--primary-font)',
        fontSize: '0.875rem',
        fontWeight: 400,
        color: 'rgba(38, 37, 37)',
        '::placeholder': {
          fontFamily: 'var(--primary-font)',
          fontSize: '0.875rem',
          fontWeight: 400,
          color: 'rgba(38, 37, 37, 0.35)',
        },
      },
      invalid: {
        color: '#D6494C',
        ':focus': {
          color: 'rgba(38, 37, 37)',
        },
      },
    },
  };

  const handleCardElementFocus = (): void => {
    Avo.subscriptionPaymentFieldFocused({
      fieldName: 'Card',
      origin: 'Subscription Screen - InputForm - Stripe Card Element',
    });
  };

  return (
    <MainContainer {...mainContainerStyles}>
      {cardElement ? (
        <CustomCardElement
          cardError={labelErrorValidations}
          onMouseEnter={(): void => setOnCardElement(true)}
          onMouseLeave={(): void => setOnCardElement(false)}
          mouseOnCardElement={onCardElement}
        >
          <CardElement
            onChange={updateValues}
            options={cardElementOptions}
            onFocus={handleCardElementFocus}
            className={styles.cardElement}
          />
        </CustomCardElement>
      ) : (
        <InputText
          {...inputStyles}
          onChange={(event): void => {
            if (setInputValue) setInputValue(event?.currentTarget.value);
            if (updateValues) updateValues(event?.currentTarget.value);
          }}
          ref={inputRef}
          name={name}
          onFocus={handleFocus}
          onBlur={handleBlur}
          value={value}
          placeholder={placeholder}
        />
      )}

      {(showLabelError || labelErrorValidations) && (
        <LabelError
          type="caption"
          colorVariant={labelErrorValidations ? 'mediumRed' : 'gray35'}
          marginTop="0.25rem"
          data-testid="labelErrorInput"
        >
          {labelError || ''}
        </LabelError>
      )}

      {name === 'password' && (
        <ShowPasswordContainer data-testid="showPasswordButton">
          <IconContainer
            showErrorLabel={!isValid && !focused}
            alwaysShowErrorLabel={showLabelError}
          >
            <Icon
              colorVariant="gray35"
              name={showPassword ? 'eyeClose' : 'eyeOpen'}
              onClick={(): void => {
                changeInputType();
              }}
            />
          </IconContainer>
        </ShowPasswordContainer>
      )}
    </MainContainer>
  );
};

InputForm.defaultProps = defaultProps;

export default InputForm;
